import { ref, computed } from 'vue'
import apiClient from '../api'
import { PRICE_HISTORY_CONFIG, getChannelColor } from '../config/priceHistory'

export function usePriceHistory() {
  const priceHistory = ref({})
  const loading = ref(false)
  const error = ref(null)
  
  // 從設定檔獲取預設值
  const DEFAULT_DAYS = PRICE_HISTORY_CONFIG.DEFAULT_DAYS
  const availablePeriods = PRICE_HISTORY_CONFIG.AVAILABLE_PERIODS
  
  const selectedPeriod = ref(DEFAULT_DAYS)
  
  // 格式化價格歷史資料供圖表使用
  const chartData = computed(() => {
    if (!priceHistory.value.price_history) return { datasets: [] }
    
    const datasets = []
    
    Object.entries(priceHistory.value.price_history).forEach(([channel, data]) => {
      // 確保 data 是陣列
      if (!Array.isArray(data)) {
        console.warn(`Price history data for channel ${channel} is not an array:`, data)
        return
      }
      
      datasets.push({
        label: channel,
        data: data.map(item => ({
          x: new Date(item.date),
          y: item.price
        })).filter(item => item.y !== null), // 過濾掉 null 價格
        borderColor: getChannelColor(channel, 'border'),
        backgroundColor: getChannelColor(channel, 'background'),
        fill: false,
        tension: 0.1,
        pointRadius: 3,
        pointHoverRadius: 5
      })
    })
    
    return { datasets }
  })
  
  // 獲取價格統計資訊
  const priceStats = computed(() => {
    if (!priceHistory.value.price_history) return null
    
    const allPrices = []
    Object.values(priceHistory.value.price_history).forEach(channelData => {
      // 確保 channelData 是陣列
      if (Array.isArray(channelData)) {
        channelData.forEach(item => {
          if (item.price !== null && item.price !== undefined) {
            allPrices.push(item.price)
          }
        })
      }
    })
    
    if (allPrices.length === 0) return null
    
    const minPrice = Math.min(...allPrices)
    const maxPrice = Math.max(...allPrices)
    const avgPrice = allPrices.reduce((sum, price) => sum + price, 0) / allPrices.length
    
    return {
      min: minPrice,
      max: maxPrice,
      average: avgPrice,
      range: maxPrice - minPrice
    }
  })
  
  // 獲取價格歷史記錄
  const fetchPriceHistory = async (productId, days = DEFAULT_DAYS) => {
    console.log('usePriceHistory: fetchPriceHistory called for productId:', productId, 'days:', days);
    loading.value = true
    error.value = null
    
    try {
      const response = await apiClient.get(`/products/${productId}/price-history`, {
        params: { days }
      })
      
      priceHistory.value = response.data
      console.log('usePriceHistory: priceHistory updated with fetched data:', JSON.stringify(priceHistory.value));
      selectedPeriod.value = days
      
      return response.data
    } catch (err) {
      error.value = err.response?.data?.detail || '無法載入價格歷史記錄'
      console.error('Price history fetch error:', err)
      throw err
    } finally {
      loading.value = false
    }
  }
  
  // 更新時間範圍
  const updatePeriod = async (productId, days) => {
    if (days !== selectedPeriod.value) {
      await fetchPriceHistory(productId, days)
    }
  }
  
  // 格式化價格
  const formatPrice = (price) => {
    return new Intl.NumberFormat('zh-TW', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(price)
  }
  
  // 格式化日期
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('zh-TW', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }
  
  // 重置狀態
  const reset = () => {
    console.log('usePriceHistory: reset called.');
    priceHistory.value = {}
    console.log('usePriceHistory: priceHistory after reset:', JSON.stringify(priceHistory.value));
    loading.value = false
    error.value = null
    selectedPeriod.value = DEFAULT_DAYS
  }
  
  return {
    // 狀態
    priceHistory,
    loading,
    error,
    selectedPeriod,
    
    // 計算屬性
    chartData,
    priceStats,
    
    // 常數
    DEFAULT_DAYS,
    availablePeriods,
    
    // 方法
    fetchPriceHistory,
    updatePeriod,
    formatPrice,
    formatDate,
    reset
  }
}